import { AppButton } from '@clickguard/design-system'
import { useState } from 'react'
import { ProductAccountDTO, AssetDTO } from 'api-models'
import { HiArrowRight } from 'react-icons/hi'
import { MdCheck } from 'react-icons/md'
import { useStores } from 'hooks'
import { Onboarding } from 'services/user'

const PhoneIcon = ({ className }: { className?: string }) => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		className={className}
	>
		<path
			d="M11.7081 4.99984C12.5221 5.15864 13.2701 5.55672 13.8565 6.14312C14.4429 6.72952 14.841 7.47756 14.9998 8.2915M11.7081 1.6665C13.3992 1.85437 14.9761 2.61165 16.18 3.81401C17.3839 5.01637 18.1431 6.59235 18.3331 8.28317M8.52228 11.5524C7.52097 10.5511 6.73031 9.41888 6.15031 8.21086C6.10042 8.10695 6.07548 8.055 6.05631 7.98926C5.98821 7.75563 6.03713 7.46875 6.17881 7.27089C6.21867 7.21521 6.2663 7.16758 6.36156 7.07232C6.6529 6.78098 6.79857 6.63531 6.89381 6.48883C7.25297 5.93642 7.25297 5.22427 6.89381 4.67186C6.79857 4.52538 6.6529 4.37971 6.36156 4.08837L6.19917 3.92598C5.7563 3.48311 5.53487 3.26168 5.29705 3.14139C4.82408 2.90217 4.26553 2.90217 3.79256 3.14139C3.55474 3.26168 3.33331 3.48311 2.89044 3.92598L2.75908 4.05735C2.31773 4.4987 2.09705 4.71937 1.92851 5.0194C1.74149 5.35232 1.60703 5.8694 1.60816 6.25125C1.60918 6.59537 1.67594 6.83056 1.80944 7.30093C2.52692 9.82876 3.88065 12.2141 5.87063 14.204C7.86061 16.194 10.2459 17.5478 12.7737 18.2652C13.2441 18.3987 13.4793 18.4655 13.8234 18.4665C14.2053 18.4677 14.7224 18.3332 15.0553 18.1462C15.3553 17.9776 15.576 17.7569 16.0173 17.3156L16.1487 17.1842C16.5916 16.7414 16.813 16.5199 16.9333 16.2821C17.1725 15.8091 17.1725 15.2506 16.9333 14.7776C16.813 14.5398 16.5916 14.3184 16.1487 13.8755L15.9863 13.7131C15.695 13.4218 15.5493 13.2761 15.4028 13.1809C14.8504 12.8217 14.1383 12.8217 13.5858 13.1809C13.4394 13.2761 13.2937 13.4218 13.0024 13.7131C12.9071 13.8084 12.8595 13.856 12.8038 13.8959C12.6059 14.0375 12.319 14.0865 12.0854 14.0184C12.0197 13.9992 11.9677 13.9743 11.8638 13.9244C10.6558 13.3444 9.5236 12.5537 8.52228 11.5524Z"
			style={{ stroke: 'var(--icon-primary)' }}
			strokeWidth="1.66667"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
)

interface OnboardingInterstitialChoiceProps {
	account: ProductAccountDTO
	asset: AssetDTO
	onScheduleCall?: () => void
	onDismiss?: () => void // Called when interstitial is dismissed (no manual tracking needed)
}

export const OnboardingInterstitialChoice = ({
	account,
	onDismiss
}: OnboardingInterstitialChoiceProps) => {
	const { accountStore } = useStores()

	const [isLoadingSetup, setIsLoadingSetup] = useState(false)
	const [isLoadingBookCall, setIsLoadingBookCall] = useState(false)

	const handleSetupMyself = async () => {
		setIsLoadingSetup(true)
		try {
			// Update database and refresh account
			if (account?.id) {
				try {
					const updatedAccount = await Onboarding.updateSetup(account.id, {
						onboardingPath: 'self-onboarding'
					})
					// Update account in store so the button appears immediately
					if (updatedAccount) {
						accountStore.setAccount(updatedAccount)
					}
				} catch (err) {
					console.error('Failed to update onboarding path:', err)
				}
			}

			// Dismiss interstitial and proceed to next step
			if (onDismiss) {
				onDismiss()
			}
		} finally {
			setIsLoadingSetup(false)
		}
	}

	const handleBookCall = async () => {
		setIsLoadingBookCall(true)
		try {
			// Update database with 'guided-onboarding'
			if (account?.id) {
				try {
					await Onboarding.updateSetup(account.id, {
						onboardingPath: 'guided-onboarding'
					})
				} catch (err) {
					console.error('Failed to update onboarding path:', err)
				}
			}

			// Navigate to HubSpot booking page
			window.location.href =
				'https://meetings.hubspot.com/thabisile-khumalo/onboarding'
		} finally {
			setIsLoadingBookCall(false)
		}
	}

	return (
		<div className="flex flex-col items-start gap-6">
			<div className="flex w-full flex-col items-center">
				<p className="text-[30px] font-medium leading-[36px] text-t-default">
					You qualify for VIP setup
				</p>
			</div>

			<div className="flex w-full flex-col gap-4">
				<div className="flex items-start gap-2">
					<MdCheck className="w-4 h-4 text-t-email-blue flex-shrink-0" />
					<p className="text-[14px] leading-4 text-t-email-blue">
						<b>No-cost</b> 1 to 1 guided setup with our specialist
					</p>
				</div>
				<div className="flex items-start gap-2">
					<MdCheck className="w-4 h-4 text-t-email-blue flex-shrink-0" />
					<p className="text-[14px] leading-4 text-t-email-blue">
						<b>Tailored</b> to your campaigns and traffic sources
					</p>
				</div>
				<div className="flex items-start gap-2">
					<MdCheck className="w-4 h-4 text-t-email-blue flex-shrink-0" />
					<p className="text-[14px] leading-4 text-t-email-blue">
						<b>Get protected faster</b>, most accounts are fully configured in a
						single call
					</p>
				</div>

				<div className="flex w-full flex-col items-center pt-4">
					<AppButton
						variant="marketing"
						size="lg"
						fullWidth
						label="Book a call with our implementation specialist"
						leftIcon={<PhoneIcon className="h-4 w-4" />}
						isLoading={isLoadingBookCall}
						loader="dots"
						onClick={handleBookCall}
					/>
				</div>
			</div>

			<div className="relative flex w-full items-center">
				<div className="flex-grow border-t border-t-checkbox" />
				<span className="shrink-0 mx-3 text-[16px] leading-[22px] text-t-gray-hold">
					OR
				</span>
				<div className="flex-grow border-t border-t-checkbox" />
			</div>

			<div className="flex w-full flex-col gap-4">
				<div className="flex w-full flex-col items-center">
					<p className="text-[20px] leading-[26px] font-medium text-t-default">
						Prefer to set it up yourself?
					</p>
				</div>
				<AppButton
					variant="secondary"
					size="lg"
					fullWidth
					label="Continue on my own"
					rightIcon={<HiArrowRight className="h-4 w-4" />}
					isLoading={isLoadingSetup}
					loader="dots"
					onClick={handleSetupMyself}
				/>
			</div>
		</div>
	)
}
